import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  Input,
  Page,
  QueryState,
  Stack,
  StatStrip,
  DocumentIcon,
  LockIcon,
  GlobeIcon,
  CheckCircleIcon,
} from '@integra/ui';
import {
  useDeleteReport,
  useGenerateReport,
  useReports,
  useShareReport,
  useUnshareReport,
} from '../api/hooks.js';
import { ReportView } from './ReportView.js';

/**
 * Client reporting + portals (§20.5, the agency VAS). Generate an immutable
 * snapshot of measured performance, then open a white-labelled portal a client
 * views without a login. The snapshot is frozen at generation, so a shared report
 * never shifts under the client — provenance-honest by construction (§2.7).
 */
export function ReportsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const reports = useReports(workspaceId);
  const generate = useGenerateReport(workspaceId);
  const share = useShareReport(workspaceId);
  const unshare = useUnshareReport(workspaceId);
  const remove = useDeleteReport(workspaceId);

  const [title, setTitle] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<{ reportId: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const list = reports.data?.data ?? [];
  const sharedCount = list.filter((r) => r.shared).length;

  function doGenerate(): void {
    if (title.trim() === '') return;
    generate.mutate({ title: title.trim() }, { onSuccess: () => setTitle('') });
  }

  function doShare(reportId: string): void {
    share.mutate(reportId, {
      onSuccess: (res) => {
        setShareLink({ reportId, url: `${window.location.origin}/r/${res.data.token}` });
        setCopied(false);
      },
    });
  }

  function copy(url: string): void {
    void navigator.clipboard?.writeText(url);
    setCopied(true);
  }

  return (
    <Page
      title="Client reports"
      icon={DocumentIcon}
      description="Generate a white-labelled snapshot of measured performance and share it on a portal your client views without a login."
      stats={
        list.length > 0 ? (
          <StatStrip
            stats={[
              { icon: DocumentIcon, label: 'Reports', value: list.length },
              { icon: GlobeIcon, label: 'Shared', value: sharedCount, tone: 'accent' },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Card
          title="Generate a report"
          subtitle="Freezes the last 30 days of measured performance as an immutable snapshot."
        >
          <Cluster gap="md" align="end">
            <Field label="Report title" htmlFor="report-title">
              <Input
                id="report-title"
                value={title}
                placeholder="March performance — Acme"
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            <Button
              loading={generate.isPending}
              disabled={title.trim() === ''}
              onClick={doGenerate}
            >
              Generate report
            </Button>
          </Cluster>
          {generate.error instanceof Error && (
            <Alert tone="error" title="Could not generate the report">
              {generate.error.message}
            </Alert>
          )}
        </Card>

        <QueryState loading={reports.isLoading} error={reports.error} what="reports">
          {list.length === 0 ? (
            <EmptyState
              icon={DocumentIcon}
              title="No reports yet"
              description="Generate one above to freeze the current measured performance and share it with a client."
              features={[
                { icon: LockIcon, label: 'Immutable snapshot, frozen at generation' },
                { icon: GlobeIcon, label: 'White-labelled portal, no client login' },
                { icon: CheckCircleIcon, label: 'Every figure keeps its provenance' },
              ]}
            />
          ) : (
            <Stack gap="md">
              {list.map((report) => (
                <Card key={report.id}>
                  <Stack gap="md">
                    <Cluster justify="between" align="center" gap="md">
                      <div className="min-w-0">
                        <Cluster gap="sm" align="center">
                          <span className="font-heading text-sm text-navy-700">{report.title}</span>
                          <Badge tone={report.shared ? 'success' : 'neutral'} dot>
                            {report.shared ? 'Shared' : 'Private'}
                          </Badge>
                        </Cluster>
                        <p className="mt-0.5 text-xs text-ink-muted">
                          {new Date(report.periodFrom).toLocaleDateString()} –{' '}
                          {new Date(report.periodTo).toLocaleDateString()} · generated{' '}
                          {new Date(report.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Cluster gap="sm">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setExpanded(expanded === report.id ? null : report.id)}
                        >
                          {expanded === report.id ? 'Hide' : 'View'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={share.isPending && share.variables === report.id}
                          onClick={() => doShare(report.id)}
                        >
                          {report.shared ? 'Re-share' : 'Share'}
                        </Button>
                        {report.shared && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={unshare.isPending && unshare.variables === report.id}
                            onClick={() => unshare.mutate(report.id)}
                          >
                            Unshare
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={remove.isPending && remove.variables === report.id}
                          onClick={() => remove.mutate(report.id)}
                        >
                          Delete
                        </Button>
                      </Cluster>
                    </Cluster>

                    {shareLink !== null && shareLink.reportId === report.id && (
                      <Alert tone="success" title="Portal link — copy it now">
                        <Cluster gap="sm" align="center">
                          <span className="break-all font-mono text-xs text-navy-700">
                            {shareLink.url}
                          </span>
                          <Button size="sm" variant="secondary" onClick={() => copy(shareLink.url)}>
                            {copied ? 'Copied' : 'Copy'}
                          </Button>
                        </Cluster>
                      </Alert>
                    )}

                    {expanded === report.id && (
                      <div className="rounded-lg border border-edge bg-surface p-4">
                        <ReportView title={report.title} snapshot={report.snapshot} />
                      </div>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </QueryState>
      </Stack>
    </Page>
  );
}
